import React from "react"
import { Logo } from "./components/Logo"
import { UserLocation } from "./components/UserLocation"

import { UserPoints } from "./components/UserPoints"
import { UsersTickets } from "./components/UserTickets"
import { Box, Button, ButtonBase, Typography } from "@mui/material"
import { keyframes } from "@mui/material/styles"
import { CompactTopBarView } from "react-ui-components/components/navigation/CompactTopBarView"
import { useNamespacedTranslation } from "./hooks/useNamespacedTranslation"
import { useUserStore } from "./components/stores/userStore"
import { useLiveDrawing } from "./hooks/useLiveDrawing"
import { useNavigate } from "react-router-dom"
import { openLocationPopup, openLoginPopup } from "./utils/lazyPopups"

export const TopToolbar: React.FC = () => {
  const { t } = useNamespacedTranslation(['common'])
  const { isLoggedIn } = useUserStore()
  const navigate = useNavigate()
  const liveDrawing = useLiveDrawing()

  const pulse = React.useMemo(
    () =>
      keyframes`
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.18); opacity: 0.7; }
        100% { transform: scale(1); opacity: 1; }
      `,
    [],
  )

  const handleOpenLiveDrawing = React.useCallback(() => {
    if (!liveDrawing?.id) return
    navigate(`/raffles/${liveDrawing.id}/drawing`)
  }, [liveDrawing?.id, navigate])

  const handleLocationClick = React.useCallback(() => {
    void openLocationPopup()
  }, [])
  const handleLoginClick = React.useCallback(() => {
    void openLoginPopup()
  }, [])

  const locationSlot = React.useMemo(
    () => (
      <UserLocation
        fullWidth
        textButton
        viewOnly
        color="secondary"
        onClick={handleLocationClick}
      />
    ),
    [handleLocationClick],
  )
  const accountSlot = React.useMemo(
    () => (
      <>
        <UsersTickets />
        <UserPoints />
      </>
    ),
    [],
  )
  const loginSlot = React.useMemo(
    () =>
      !isLoggedIn ? (
        <Button
          variant="text"
          className="text-white smooth-transition button-hover"
          onClick={handleLoginClick}
          aria-label={t("Login")}
          sx={{
            minHeight: 32,
            minWidth: { xs: "auto", sm: 72 },
            px: { xs: 1.25, sm: 1.75 },
            py: 0,
            lineHeight: 1,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Typography
            variant="button"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, lineHeight: 1.1 }}
          >
            {t("Login")}
          </Typography>
        </Button>
      ) : null,
    [handleLoginClick, isLoggedIn, t],
  )

  const logoSlot = React.useMemo(
    () =>
      liveDrawing ? (
        <ButtonBase
          onClick={handleOpenLiveDrawing}
          aria-label={t("Winfacil Live")}
          sx={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
            borderRadius: 1,
            overflow: "visible",
            color: "inherit",
            "&:focus-visible": {
              outline: "2px solid currentColor",
              outlineOffset: 4,
            },
          }}
        >
          <Logo
            width="clamp(102px, 11vw, 118px)"
            height={40}
            cropped
            loading="eager"
          />
          <Box
            component="span"
            sx={{
              position: "absolute",
              left: "50%",
              bottom: 0,
              transform: "translateX(-50%)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 44,
              height: 12,
              px: 0.45,
              borderRadius: 999,
              backgroundColor: "error.main",
              color: "common.white",
              whiteSpace: "nowrap",
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                backgroundColor: "common.white",
                mr: 0.3,
                animation: `${pulse} 1s ease-in-out infinite`,
              }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: "0.42rem",
                fontWeight: 800,
                lineHeight: 1,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {t("Live")}
            </Typography>
          </Box>
        </ButtonBase>
      ) : (
        <Logo
          width="clamp(108px, 12vw, 124px)"
          height={48}
          cropped
          loading="eager"
        />
      ),
    [handleOpenLiveDrawing, liveDrawing, pulse, t],
  )

  return (
    <CompactTopBarView
      row="top"
      logoSlot={logoSlot}
      locationSlot={locationSlot}
      accountSlot={accountSlot}
      loginSlot={loginSlot}
    />
  )
}
